import { useState, useEffect, useRef } from "react";
import {
  FaEnvelope,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaUser,
  FaCalendar,
  FaClock,
} from "react-icons/fa";
import { useNotifications } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import InlineAlert from "../../components/UI/DesignStandards/Notifications/InlineAlert";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { formatShortDate } from "../../components/UI/DesignStandards";

const Invites = () => {
  const { pendingInvites, refreshInvites, markInviteAsViewed } =
    useNotifications();
  const navigate = useNavigate();
  const [processingInvites, setProcessingInvites] = useState(new Set());
  const viewedInvitesRef = useRef(new Set());
  const [errorMessage, setErrorMessage] = useState("");

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Invitations" },
  ];

  // Mark all invitations as viewed when the page loads
  useEffect(() => {
    if (pendingInvites && pendingInvites.length > 0) {
      pendingInvites.forEach((invite) => {
        if (!viewedInvitesRef.current.has(invite.id)) {
          markInviteAsViewed(invite.id);
          viewedInvitesRef.current.add(invite.id);
        }
      });
    }
  }, [pendingInvites]); // Remove markInviteAsViewed from dependencies

  const handleAcceptInvite = (invite) => {
    navigate(`/invite/${invite.id}`);
  };

  const handleRejectInvite = async (inviteId) => {
    setProcessingInvites((prev) => new Set(prev).add(inviteId));

    try {
      const { error } = await supabase
        .from("organizationInvites")
        .update({
          status: "declined",
          seenAt: new Date().toISOString(),
        })
        .eq("id", inviteId);

      if (error) throw error;
      refreshInvites();
    } catch (error) {
      console.error("Error declining invite:", error);
      setErrorMessage("Failed to decline invitation");
    } finally {
      setProcessingInvites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(inviteId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    // Use design standards formatShortDate which properly handles timezone issues
    return formatShortDate(dateString);
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  };

  if (!pendingInvites || pendingInvites.length === 0) {
    return (
      <div className="min-h-screen bg-brand-cream py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaEnvelope className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No Pending Invitations
            </h2>
            <p className="text-gray-600">
              You don't have any pending organization invitations at the moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {errorMessage ? (
          <div className="mb-4">
            <InlineAlert
              type="error"
              title="There was a problem"
              message={errorMessage}
              onClose={() => setErrorMessage("")}
            />
          </div>
        ) : null}

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Organization Invitations
          </h1>
          <p className="mt-2 text-gray-600">
            You have {pendingInvites.length} pending invitation
            {pendingInvites.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="space-y-4">
          {pendingInvites.map((invite) => (
            <div key={invite.id} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Organization Info */}
                  <div className="flex items-center mb-3">
                    <div className="h-12 w-12 bg-brand-cream rounded-full flex items-center justify-center mr-4">
                      <FaEnvelope className="h-6 w-6 text-brand-gold-ink" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {invite.organization.name}
                        {invite.organization.nameAbbreviation && (
                          <span className="text-gray-500 ml-2">
                            ({invite.organization.nameAbbreviation})
                          </span>
                        )}
                      </h3>
                      {invite.organization.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {invite.organization.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Invitation Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaUser className="h-4 w-4 mr-2 text-gray-400" />
                      <span>
                        Invited by:{" "}
                        {invite.invitedByUser?.full_name ||
                          invite.invitedByUser?.email ||
                          "Unknown"}
                        {invite.invitedByUser?.email &&
                          invite.invitedByUser?.full_name &&
                          ` (${invite.invitedByUser.email})`}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaCalendar className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Invited {formatTimeAgo(invite.createdAt)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaClock className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Expires {formatDate(invite.expiredAt)}</span>
                    </div>
                  </div>

                  {/* Expiration Warning */}
                  {new Date(invite.expiredAt) <
                    new Date(Date.now() + 24 * 60 * 60 * 1000) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                      <div className="flex">
                        <FaClock className="h-5 w-5 text-yellow-400 mt-0.5" />
                        <div className="ml-3">
                          <p className="text-sm text-yellow-800">
                            This invitation expires soon. Please respond before{" "}
                            {formatDate(invite.expiredAt)}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-2 ml-6">
                  <button
                    onClick={() => handleAcceptInvite(invite)}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-display font-bold tracking-wide text-brand-charcoal bg-brand-gold hover:bg-brand-gold/90"
                  >
                    <FaCheck className="h-4 w-4 mr-2" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleRejectInvite(invite.id)}
                    disabled={processingInvites.has(invite.id)}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-display font-bold tracking-wide text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingInvites.has(invite.id) ? (
                      <>
                        <FaSpinner className="h-4 w-4 mr-2 animate-spin" />
                        Declining...
                      </>
                    ) : (
                      <>
                        <FaTimes className="h-4 w-4 mr-2" />
                        Decline
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Invites;
